import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { RefreshCw, Trash2, Upload, FileText, Sparkles, Download, Pencil, Smartphone } from 'lucide-react';
import { Button, ConfirmDialog, ThemedText } from '../../components';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../../components/TableStyles';
import { ModalOverlay as Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../../components/ModalStyles';
import { FormGroup, FormLabel as Label } from '../../components/FormStyles';
import {
  knowledgeBaseService,
  KNOWLEDGE_DOCUMENT_STATUS_LABELS,
} from '../../api';
import { renderFormIcon } from '../../utils/iconUtils';

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(p) => p.theme.spacing.md};
  margin-bottom: ${(p) => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const ToolbarRight = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

const TopicChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  margin-right: 4px;
  margin-bottom: 2px;
  border-radius: 10px;
  background: ${(p) => p.theme.colors.backgroundSecondary};
  color: ${(p) => p.theme.colors.text};
  font-size: 11px;
  border: 1px solid ${(p) => p.theme.colors.border};
`;

const HelpText = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.colors.muted};
  margin-top: 4px;
`;

const StatusColors = {
  Uploaded: { bg: '#fef3c7', color: '#92400e' },
  Processing: { bg: '#dbeafe', color: '#1e40af' },
  Indexed: { bg: '#dcfce7', color: '#166534' },
  Failed: { bg: '#fee2e2', color: '#991b1b' },
  PendingDeletion: { bg: '#f1f5f9', color: '#475569' },
};

const formatBytes = (bytes) => {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background};
  color: ${(p) => p.theme.colors.text};
  font-size: 13px;
`;

const TopicSelectorList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  padding: 8px;
  background: ${(p) => p.theme.colors.background};
`;

const TopicSelectorRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: ${(p) => p.theme.borderRadius.sm};
  cursor: pointer;
  font-size: 13px;
  color: ${(p) => p.theme.colors.text};
  &:hover { background: ${(p) => p.theme.colors.backgroundSecondary}; }
`;

function flattenTopicsForSelector(nodes, depth = 0, list = []) {
  nodes.forEach((t) => {
    list.push({ id: t.id, name: t.name, icon: t.icon, depth, isVisibleInMobile: t.isVisibleInMobile });
    if (t.children?.length) flattenTopicsForSelector(t.children, depth + 1, list);
  });
  return list;
}

export default function KnowledgeDocumentsView({ organizationId, canManage, theme }) {
  const [docs, setDocs] = useState([]);
  const [topicsTree, setTopicsTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    fileName: '',
    contentType: '',
    fileSize: 0,
    title: '',
    description: '',
    file: null,
    topicIds: [],
  });
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', tags: '', topicIds: [] });
  const [editBusy, setEditBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const fileInputRef = useRef(null);

  const flatTopics = useMemo(() => flattenTopicsForSelector(topicsTree), [topicsTree]);
  const topicById = useMemo(() => {
    const map = new Map();
    flatTopics.forEach((t) => map.set(t.id, t));
    return map;
  }, [flatTopics]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, topics] = await Promise.all([
        knowledgeBaseService.listDocuments(organizationId),
        knowledgeBaseService.getTopics(organizationId),
      ]);
      setDocs(list || []);
      setTopicsTree(topics || []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadForm((prev) => ({
      ...prev,
      file: f,
      fileName: f.name,
      contentType: f.type || 'application/octet-stream',
      fileSize: f.size,
      title: prev.title || f.name.replace(/\.[^.]+$/, ''),
    }));
  };

  const handleAnalyze = async () => {
    if (!uploadForm.fileName) return;
    try {
      const result = await knowledgeBaseService.analyzeDocument(organizationId, {
        fileName: uploadForm.fileName,
        contentType: uploadForm.contentType,
      });
      setUploadForm((prev) => ({
        ...prev,
        title: result.title || prev.title,
        description: result.description || prev.description,
      }));
      toast.success('Suggestions applied');
    } catch (err) {
      toast.error(err?.message || 'Analyze failed');
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error('Pick a file first');
      return;
    }
    setUploading(true);
    let created = null;
    try {
      created = await knowledgeBaseService.createDocument(organizationId, {
        fileName: uploadForm.fileName,
        contentType: uploadForm.contentType,
        fileSize: uploadForm.fileSize,
        title: uploadForm.title || null,
        description: uploadForm.description || null,
        tags: [],
        topicIds: uploadForm.topicIds,
      });
      try {
        await knowledgeBaseService.uploadFileToPresigned(created.uploadUrl, uploadForm.file, uploadForm.contentType);
      } catch (uploadErr) {
        // Rollback the orphan DB row so users don't see "Pending sync" entries with no S3 content.
        try { await knowledgeBaseService.deleteDocument(organizationId, created.documentId); } catch (_) { /* best effort */ }
        throw uploadErr;
      }
      toast.success('Uploaded. Click "Sync" to index it.');
      setUploadOpen(false);
      setUploadForm({
        fileName: '',
        contentType: '',
        fileSize: 0,
        title: '',
        description: '',
        file: null,
        topicIds: [],
      });
      await load();
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await knowledgeBaseService.deleteDocument(organizationId, deleteTarget.id);
      toast.success('Document deleted');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Delete failed');
    }
  };

  const openEdit = (doc) => {
    setEditTarget(doc);
    setEditForm({
      title: doc.title || '',
      description: doc.description || '',
      tags: (doc.tags || []).join(', '),
      topicIds: [...(doc.topicIds || [])],
    });
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setEditBusy(true);
    try {
      const tagList = editForm.tags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      await knowledgeBaseService.updateDocument(organizationId, editTarget.id, {
        title: editForm.title || null,
        description: editForm.description || null,
        tags: tagList,
      });
      // Topic assignments are a separate endpoint
      const original = (editTarget.topicIds || []).slice().sort().join(',');
      const next = editForm.topicIds.slice().sort().join(',');
      if (original !== next) {
        await knowledgeBaseService.assignTopics(organizationId, editTarget.id, editForm.topicIds);
      }
      toast.success('Document updated');
      setEditTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Update failed');
    } finally {
      setEditBusy(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await knowledgeBaseService.triggerSync(organizationId);
      toast.success(`Sync started (job ${result.ingestionJobId.slice(0, 8)}…)`);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const { url } = await knowledgeBaseService.getDocumentDownloadUrl(organizationId, doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err?.message || 'Could not generate download URL');
    }
  };

  const toggleTopicId = (formSetter) => (id) => {
    formSetter((prev) => {
      const has = prev.topicIds.includes(id);
      return { ...prev, topicIds: has ? prev.topicIds.filter((x) => x !== id) : [...prev.topicIds, id] };
    });
  };

  const renderTopicSelector = (selectedIds, onToggle) => (
    <TopicSelectorList theme={theme}>
      {flatTopics.length === 0 && (
        <div style={{ fontSize: 12, color: theme.colors.muted, padding: 8 }}>
          No topics yet — create some in the Topics tab to organize documents.
        </div>
      )}
      {flatTopics.map((t) => (
        <TopicSelectorRow theme={theme} key={t.id}>
          <input
            type="checkbox"
            checked={selectedIds.includes(t.id)}
            onChange={() => onToggle(t.id)}
          />
          <span style={{ marginLeft: t.depth * 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {renderFormIcon(t.icon || 'file-text', 14, { color: theme.colors.muted })}
            {t.name}
            {t.isVisibleInMobile && <Smartphone size={12} style={{ color: theme.colors.muted }} />}
          </span>
        </TopicSelectorRow>
      ))}
    </TopicSelectorList>
  );

  return (
    <>
      <Toolbar theme={theme}>
        <ThemedText type="default" style={{ color: theme.colors.muted, fontSize: 13 }}>
          {loading ? 'Loading…' : `${docs.length} document${docs.length === 1 ? '' : 's'}`}
        </ThemedText>
        <ToolbarRight>
          <Button variant="secondary" size="sm" onClick={load}>
            <RefreshCw size={14} /> Refresh
          </Button>
          {canManage && (
            <>
              <Button variant="secondary" size="sm" onClick={handleSync} disabled={syncing}>
                <RefreshCw size={14} /> {syncing ? 'Syncing…' : 'Sync now'}
              </Button>
              <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>
                <Upload size={14} /> Upload
              </Button>
            </>
          )}
        </ToolbarRight>
      </Toolbar>

      <TableContainer theme={theme}>
        <Table theme={theme}>
          <Thead theme={theme}>
            <Tr theme={theme}>
              <Th theme={theme}>Name</Th>
              <Th theme={theme}>Topics</Th>
              <Th theme={theme}>Size</Th>
              <Th theme={theme}>Status</Th>
              <Th theme={theme}>Uploaded</Th>
              <Th theme={theme} style={{ textAlign: 'right' }}>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loading && docs.length === 0 && (
              <Tr theme={theme}>
                <Td theme={theme} colSpan={6} style={{ textAlign: 'center', padding: 32 }}>
                  No documents yet. {canManage ? 'Click Upload to add the first one.' : ''}
                </Td>
              </Tr>
            )}
            {docs.map((d) => {
              const statusKey = d.status;
              const sc = StatusColors[statusKey] || StatusColors.Uploaded;
              const docTopics = (d.topicIds || []).map((id) => topicById.get(id)).filter(Boolean);
              return (
                <Tr theme={theme} key={d.id}>
                  <Td theme={theme}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileText size={14} style={{ flexShrink: 0, color: theme.colors.muted }} />
                      <div>
                        <div style={{ fontWeight: 500 }}>{d.title || d.fileName}</div>
                        {d.title && d.title !== d.fileName && (
                          <div style={{ fontSize: 11, color: theme.colors.muted }}>{d.fileName}</div>
                        )}
                      </div>
                    </div>
                  </Td>
                  <Td theme={theme}>
                    {docTopics.length === 0 ? (
                      <span style={{ fontSize: 11, color: theme.colors.muted }}>—</span>
                    ) : (
                      docTopics.map((t) => (
                        <TopicChip key={t.id} theme={theme}>
                          {renderFormIcon(t.icon || 'file-text', 11, { color: theme.colors.muted })}
                          {t.name}
                        </TopicChip>
                      ))
                    )}
                  </Td>
                  <Td theme={theme}>{formatBytes(d.fileSize)}</Td>
                  <Td theme={theme}>
                    <StatusBadge $bg={sc.bg} $color={sc.color}>
                      {KNOWLEDGE_DOCUMENT_STATUS_LABELS[statusKey] || statusKey}
                    </StatusBadge>
                    {d.status === 'Failed' && d.ingestionError && (
                      <div style={{ fontSize: 10, color: '#991b1b', marginTop: 2 }}>{d.ingestionError}</div>
                    )}
                  </Td>
                  <Td theme={theme} style={{ fontSize: 12, color: theme.colors.muted }}>
                    {d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '—'}
                  </Td>
                  <Td theme={theme} style={{ textAlign: 'right' }}>
                    <Button variant="secondary" size="sm" onClick={() => handleDownload(d)} title="Download">
                      <Download size={14} />
                    </Button>
                    {canManage && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openEdit(d)}
                          title="Edit"
                          style={{ marginLeft: 6 }}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setDeleteTarget(d)}
                          title="Delete"
                          style={{ marginLeft: 6 }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Upload modal */}
      <Modal theme={theme} isOpen={uploadOpen}>
        <ModalContent theme={theme}>
          <ModalHeader theme={theme}>
            <ThemedText type="subtitle">Upload document</ThemedText>
          </ModalHeader>
          <ModalBody theme={theme}>
            <FormGroup theme={theme}>
              <Label theme={theme}>File</Label>
              <input ref={fileInputRef} type="file" onChange={onFilePicked} />
              {uploadForm.fileName && (
                <HelpText theme={theme}>
                  {uploadForm.fileName} · {formatBytes(uploadForm.fileSize)} · {uploadForm.contentType}
                </HelpText>
              )}
            </FormGroup>
            <FormGroup theme={theme}>
              <Label theme={theme}>Title</Label>
              <Input
                theme={theme}
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              />
            </FormGroup>
            <FormGroup theme={theme}>
              <Label theme={theme}>Description</Label>
              <Input
                theme={theme}
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              />
            </FormGroup>
            <FormGroup theme={theme}>
              <Label theme={theme}>Topics</Label>
              {renderTopicSelector(uploadForm.topicIds, toggleTopicId(setUploadForm))}
            </FormGroup>
            <Button variant="secondary" size="sm" onClick={handleAnalyze} disabled={!uploadForm.fileName}>
              <Sparkles size={14} /> Suggest title from filename
            </Button>
          </ModalBody>
          <ModalFooter theme={theme}>
            <Button variant="secondary" size="sm" onClick={() => setUploadOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleUpload} disabled={uploading || !uploadForm.file}>
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit modal */}
      <Modal theme={theme} isOpen={!!editTarget}>
        <ModalContent theme={theme}>
          <ModalHeader theme={theme}>
            <ThemedText type="subtitle">Edit document</ThemedText>
          </ModalHeader>
          <ModalBody theme={theme}>
            {editTarget && (
              <>
                <FormGroup theme={theme}>
                  <Label theme={theme}>File</Label>
                  <HelpText theme={theme}>{editTarget.fileName}</HelpText>
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Title</Label>
                  <Input
                    theme={theme}
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Description</Label>
                  <Input
                    theme={theme}
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Tags (comma-separated)</Label>
                  <Input
                    theme={theme}
                    value={editForm.tags}
                    onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                    placeholder="finance, 2026, refund"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Topics</Label>
                  {renderTopicSelector(editForm.topicIds, toggleTopicId(setEditForm))}
                </FormGroup>
              </>
            )}
          </ModalBody>
          <ModalFooter theme={theme}>
            <Button variant="secondary" size="sm" onClick={() => setEditTarget(null)} disabled={editBusy}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSaveEdit} disabled={editBusy}>
              {editBusy ? 'Saving…' : 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete document?"
        message={`This will permanently remove "${deleteTarget?.title || deleteTarget?.fileName}" from the knowledge base. The next sync will remove it from search results.`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
